/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { formatDate } from '@angular/common';

const SEC_IN_MS = 1000;
export const MIN_IN_MS = 60 * SEC_IN_MS;
export const HOUR_IN_MS = 60 * MIN_IN_MS;
export const DAY_IN_MS = 24 * HOUR_IN_MS;
const DAY_IN_NS = DAY_IN_MS * 1000;
const NANOSECONDS_IN_MILLISECOND = 1_000_000;

export const SHORT_DATE_FORMAT = `dd MMM y`;
export const LONG_DATE_FORMAT = `MMMM d, y 'at' H:mm:ss.SSS z`;
export const DATE_WITH_DAYS_FORMAT = 'MMM d';
export const DATE_WITH_HOURS_FORMAT = 'MMM d, H:mm';
export const DATE_WITH_SECONDS_FORMAT = 'MMM d, H:mm:ss';
export const DATE_WITH_MILLISECONDS_FORMAT = 'MMM d, H:mm:ss.SSS';

/**
 * convert time in milliseconds to xxd xxh xxm xxs xxxms format
 *
 * @param value time in milliseconds
 */
export const fullTime = (value: number) =>
  isNaN(value)
    ? ''
    : (
        Math.trunc(value / DAY_IN_MS) +
        ' d ' +
        formatDate(value, `H' h' mm' m' ss' s' SSS' ms'`, 'en-US', '+0000')
      ).replace(/^0[^1-9]+/, '') || '0 ms';

/**
 * convert time in nanoseconds to xxd xxh xxm xxs xxxms xxxns format
 *
 * @param value time in nanoseconds
 */
export const fullTimeFromNs = (value: number) =>
  isNaN(value)
    ? ''
    : (
        Math.trunc(value / DAY_IN_NS) +
        ' d ' +
        formatDate(Math.trunc(+value / 1000), `H' h' mm' m' ss' s' SSS' ms' `, 'en-US', '+0000') +
        ((value % 1000) + ' ns').padStart(6, '0')
      ).replace(/^0[^1-9]+/, '') || '0 ns';

/**
 * convert time in milliseconds to xxd xxh / xxh xxm / xxm xxs / xxs / xxxms format
 *
 * @param value time in milliseconds
 */
export const mediumTime = (value: number) => {
  if (isNaN(value)) return '';
  if (value >= DAY_IN_MS) return Math.trunc(value / DAY_IN_MS) + ' d ' + formatDate(value, `H' h'`, 'en-US', '+0000');
  if (value >= HOUR_IN_MS) return formatDate(value, `H' h' mm' m'`, 'en-US', '+0000');
  if (value >= MIN_IN_MS) return formatDate(value, `m' m' ss' s'`, 'en-US', '+0000');
  if (value >= SEC_IN_MS) return formatDate(value, `s' s'`, 'en-US', '+0000');
  return Math.trunc(value) + ' ms';
};

/**
 * convert time in milliseconds to xxd / xxh / xxm / xxs / xxxms format
 *
 * @param value time in milliseconds
 */
export const shortTime = (value: number) => {
  if (isNaN(value)) return '';
  if (value < SEC_IN_MS) return value + ' ms';
  if (value < MIN_IN_MS) return Math.trunc(value / SEC_IN_MS) + ' s';
  if (value < HOUR_IN_MS) return Math.trunc(value / MIN_IN_MS) + ' m';
  if (value < DAY_IN_MS) return Math.trunc(value / HOUR_IN_MS) + ' h';
  else return Math.trunc(value / DAY_IN_MS) + ' d';
};

export const shortDateFromTimestamp = (value: number) =>
  isNaN(value) ? '' : formatDate(value, SHORT_DATE_FORMAT, 'en-US');
export const dateWithHoursFromTimestamp = (value: number) =>
  isNaN(value) ? '' : formatDate(value, DATE_WITH_HOURS_FORMAT, 'en-US');
export const dateWithSecondsFromTimestamp = (value: number) =>
  isNaN(value) ? '' : formatDate(value, DATE_WITH_SECONDS_FORMAT, 'en-US');
export const dateWithMillisecondsFromTimestamp = (value: number) =>
  isNaN(value) ? '' : formatDate(value, DATE_WITH_MILLISECONDS_FORMAT, 'en-US');
export const fullDateFromTimestamp = (value: number) =>
  isNaN(value) ? '' : formatDate(value, LONG_DATE_FORMAT, 'en-US');
export const nsToMs = (nanoseconds: number) => nanoseconds / NANOSECONDS_IN_MILLISECOND;

export const parseAsMs = (value: string): number | null => {
  const result = value.endsWith(' s')
    ? +value.replace(' s', '') * 1_000
    : value.endsWith(' ms')
      ? +value.replace(' ms', '')
      : null;
  return Number.isNaN(result) ? null : result;
};

export const getDaysLeft = (utcTimestamp: number): number => {
  const oneDay = 24 * 60 * 60 * 1000;
  const daysBefore = Math.round((utcTimestamp - Date.now()) / oneDay);
  return daysBefore < 0 ? 0 : daysBefore;
};

export const millisecondsToSeconds = (ms: number): number => ms / SEC_IN_MS;
export const millisecondsToMinutes = (ms: number): number => ms / MIN_IN_MS;
export const secondsToMilliseconds = (seconds: number): number => seconds * SEC_IN_MS;
export const minutesToMilliseconds = (minutes: number): number => minutes * MIN_IN_MS;
